import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { environment } from "../../../../environments/environment";
import {
  PageResponse,
  Segment,
  SegmentRequest,
  SegmentSearchFilters,
  SegmentMemberResponse,
  SegmentScheduleRequest,
  SegmentScheduleResponse,
  SegmentScheduleEnabledRequest,
  SegmentSetOperationRequest,
  SegmentSetOperationResponse,
  SegmentImportResponse,
  SegmentBulkActionRequest,
  SegmentBulkActionResponse,
  FilterRuleRequest,
  FilterRuleSummary,
  SegmentSeedResponse,
  SegmentStatsResponse,
  SegmentPreviewRequest,
  SegmentPreviewContactResponse
} from '../models/segment.model';

@Injectable({
  providedIn: 'root'
})
export class SegmentService {
  private readonly baseUrl = `${environment.apiUrl}api/segments`;

  constructor(private http: HttpClient) { }

  // =============================================
  // SEGMENTS CRUD
  // =============================================

  getSegments(page: number = 0, size: number = 20, filters?: SegmentSearchFilters): Observable<PageResponse<Segment>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (filters?.type) params = params.set('type', filters.type);
    if (filters?.status) params = params.set('status', filters.status);
    if (filters?.folderId) params = params.set('folderId', filters.folderId.toString());
    if (filters?.searchTerm) params = params.set('searchTerm', filters.searchTerm);

    return this.http.get<PageResponse<Segment>>(`${this.baseUrl}/segment`, { params });
  }

  getSegmentById(id: number): Observable<Segment> {
    return this.http.get<Segment>(`${this.baseUrl}/segment/${id}`);
  }

  createSegment(request: SegmentRequest): Observable<Segment> {
    return this.http.post<Segment>(`${this.baseUrl}/segment`, request);
  }

  updateSegment(id: number, request: SegmentRequest): Observable<Segment> {
    return this.http.put<Segment>(`${this.baseUrl}/segment/${id}`, request);
  }

  deleteSegment(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/segment/${id}`);
  }

  evaluateSegment(id: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/segment/${id}/evaluate`, {});
  }

  seedFromRules(id: number): Observable<SegmentSeedResponse> {
    return this.http.post<SegmentSeedResponse>(`${this.baseUrl}/segment/${id}/seed-from-rules`, {});
  }

  duplicateSegment(id: number): Observable<Segment> {
    return this.http.post<Segment>(`${this.baseUrl}/segment/${id}/duplicate`, {});
  }

  moveToFolder(segmentId: number, folderId: number | null): Observable<Segment> {
    return this.http.patch<Segment>(`${this.baseUrl}/segment/${segmentId}/folder`, { folderId });
  }

  updateStatus(id: number, status: 'DRAFT' | 'READY' | 'ARCHIVED' | 'SUPPRESSED'): Observable<Segment> {
    return this.http.patch<Segment>(`${this.baseUrl}/segment/${id}/status`, { status });
  }

  changeSegmentType(id: number, newType: 'STATIC' | 'DYNAMIC'): Observable<Segment> {
    return this.http.patch<Segment>(`${this.baseUrl}/segment/${id}/type`, { newType });
  }

  getStats(): Observable<SegmentStatsResponse> {
    return this.http.get<SegmentStatsResponse>(`${this.baseUrl}/segment/stats`);
  }

  // =============================================
  // MEMBERS
  // =============================================

  getMembers(segmentId: number, page: number = 0, size: number = 20, search?: string): Observable<PageResponse<SegmentMemberResponse>> {
    let params = new HttpParams()
      .set('segmentId', segmentId.toString())
      .set('page', page.toString())
      .set('size', size.toString());
    if (search && search.trim()) params = params.set('search', search.trim());
    return this.http.get<PageResponse<SegmentMemberResponse>>(`${this.baseUrl}/members`, { params });
  }

  getExcludedMembers(segmentId: number, page: number = 0, size: number = 20): Observable<PageResponse<SegmentMemberResponse>> {
    const params = new HttpParams()
      .set('segmentId', segmentId.toString())
      .set('page', page.toString())
      .set('size', size.toString())
      .set('excludedOnly', 'true');
    return this.http.get<PageResponse<SegmentMemberResponse>>(`${this.baseUrl}/members`, { params });
  }

  reincludeMembers(segmentId: number, contactIds: number[], userActionId: number): Observable<{ total: number; added: number; duplicates: number; reactivated: number }> {
    return this.http.post<{ total: number; added: number; duplicates: number; reactivated: number }>(
      `${this.baseUrl}/members/batch`, { contactSegmentId: segmentId, contactIds, userActionId }
    );
  }

  excludeMember(segmentId: number, contactId: number, userActionId: number): Observable<void> {
    const params = new HttpParams()
      .set('segmentId', segmentId.toString())
      .set('contactId', contactId.toString())
      .set('userActionId', userActionId.toString());
    return this.http.delete<void>(`${this.baseUrl}/members`, { params });
  }

  // =============================================
  // IMPORT & BULK ACTIONS
  // =============================================

  importMembers(segmentId: number, file: File): Observable<SegmentImportResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<SegmentImportResponse>(`${this.baseUrl}/segment/${segmentId}/import`, formData);
  }

  executeBulkAction(segmentId: number, request: SegmentBulkActionRequest): Observable<SegmentBulkActionResponse> {
    return this.http.post<SegmentBulkActionResponse>(`${this.baseUrl}/segment/${segmentId}/bulk-action`, request);
  }

  // =============================================
  // SCHEDULES
  // =============================================

  getSchedule(contactSegmentId: number): Observable<SegmentScheduleResponse> {
    const params = new HttpParams().set('contactSegmentId', contactSegmentId.toString());
    return this.http.get<SegmentScheduleResponse>(`${this.baseUrl}/schedules`, { params });
  }

  createSchedule(request: SegmentScheduleRequest): Observable<SegmentScheduleResponse> {
    return this.http.post<SegmentScheduleResponse>(`${this.baseUrl}/schedules`, request);
  }

  updateSchedule(id: number, request: SegmentScheduleRequest): Observable<SegmentScheduleResponse> {
    return this.http.put<SegmentScheduleResponse>(`${this.baseUrl}/schedules/${id}`, request);
  }

  deleteSchedule(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/schedules/${id}`);
  }

  setScheduleEnabled(id: number, enabled: boolean): Observable<SegmentScheduleResponse> {
    const body: SegmentScheduleEnabledRequest = { enabled };
    return this.http.patch<SegmentScheduleResponse>(`${this.baseUrl}/schedules/${id}/enabled`, body);
  }

  // =============================================
  // FILTER RULES
  // =============================================

  getRulesBySegment(segmentId: number): Observable<FilterRuleSummary[]> {
    const params = new HttpParams().set('segmentId', segmentId.toString());
    return this.http.get<FilterRuleSummary[]>(`${this.baseUrl}/filter-rules`, { params });
  }

  createRule(request: FilterRuleRequest): Observable<FilterRuleSummary> {
    return this.http.post<FilterRuleSummary>(`${this.baseUrl}/filter-rules`, request);
  }

  deleteRule(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/filter-rules/${id}`);
  }

  // =============================================
  // PREVIEW
  // =============================================

  previewSegment(request: SegmentPreviewRequest, page = 0, size = 20): Observable<PageResponse<SegmentPreviewContactResponse>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.post<PageResponse<SegmentPreviewContactResponse>>(`${this.baseUrl}/segment/preview`, request, { params });
  }

  excludeNotMatching(segmentId: number, request: SegmentPreviewRequest): Observable<{ segmentId: number; excluded: number; totalActiveMembers: number }> {
    return this.http.post<{ segmentId: number; excluded: number; totalActiveMembers: number }>(
      `${this.baseUrl}/segment/${segmentId}/exclude-not-matching`, request
    );
  }

  previewNotMatching(segmentId: number, request: SegmentPreviewRequest, page = 0, size = 20): Observable<PageResponse<SegmentPreviewContactResponse>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.post<PageResponse<SegmentPreviewContactResponse>>(`${this.baseUrl}/segment/${segmentId}/preview-not-matching`, request, { params });
  }

  // =============================================
  // SET OPERATIONS
  // =============================================

  getSetOperations(resultSegmentId: number): Observable<SegmentSetOperationResponse[]> {
    return this.http.get<SegmentSetOperationResponse[]>(`${this.baseUrl}/set-operations/${resultSegmentId}`);
  }

  executeSetOperation(resultSegmentId: number, request: SegmentSetOperationRequest): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/set-operations/${resultSegmentId}`, request);
  }
}
